'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var Icon = require('../../Icon/src/Icon.js');
var Spinner = require('../../Spinner/src/Spinner.js');
var TextInput = require('../../TextInput/src/TextInput.js');
var Textarea_module = require('../styles/Textarea.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

const MIN_ROWS = 1;
const MAX_ROWS = 10;
/**
 * @description The Text Area component allow user to type in longer content
 *
 * @component
 * @example
 * ```jsx
 * <Textarea value="content" />
 * ```
 */
const Textarea = React__namespace.forwardRef((props, ref) => {
    const { id, value, label, placeholder, resizable, icon, disabled, invalid, readOnly, loading, rows, positionInGroup, directionGroup, background, onFocus, onBlur, onChange, onClick, style, className, ...restProps } = props;
    const cx = reactUtils.useStyles(Textarea_module.default);
    const formControlContext = reactUtils.useFormControlContext();
    const inputRef = React__namespace.useRef();
    const containerRef = React__namespace.useRef(null);
    const [focused, setFocused] = reactUtils.useBoolean(false);
    const [filled, setFilled] = reactUtils.useBoolean(value !== undefined && value !== '');
    const [innerRows, setInnerRows] = React__namespace.useState(rows || 1);
    const inputId = reactUtils.useId(id ? '' : 'textarea', id);
    const hasIcon = icon || loading;
    const isFocused = focused && !readOnly;
    const isDisabled = formControlContext.disabled || disabled;
    const isInvalid = formControlContext.invalid || invalid;
    const classNames = cx(className, 'container', {
        focused,
        disabled: formControlContext.disabled || disabled,
        invalid: formControlContext.invalid || invalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-label': reactUtils.isDefined(label),
        [`background-${background}`]: background,
        [`group-${directionGroup}`]: directionGroup,
        [`group-${positionInGroup}`]: positionInGroup,
    });
    const classNamesLabel = cx('label', {
        focused: isFocused,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
    });
    const classNamesPlaceholder = cx('placeholder', {
        focused: isFocused,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        withLabel: label && focused,
        hidden: label && !focused,
    });
    const classNamesTextarea = cx('textarea', {
        focused: focused && !readOnly,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        resize: resizable,
        scroll: innerRows === MAX_ROWS,
    });
    const iconElement = React__namespace.useMemo(() => {
        if (loading) {
            return React__namespace.createElement(Spinner.Spinner, { className: cx('icon') });
        }
        if (icon) {
            return React__namespace.createElement(Icon.Icon, { className: cx('icon'), icon: icon });
        }
        return null;
    }, [cx, icon, loading]);
    const labelElement = React__namespace.useMemo(() => {
        if (label) {
            return (React__namespace.createElement("label", { htmlFor: inputId, className: classNamesLabel }, label));
        }
        return null;
    }, [inputId, label, classNamesLabel]);
    const placeholderElement = React__namespace.useMemo(() => {
        if ((label && readOnly) || filled) {
            return null;
        }
        if (placeholder) {
            return (React__namespace.createElement("label", { htmlFor: inputId, className: classNamesPlaceholder }, placeholder));
        }
        return null;
    }, [inputId, label, placeholder, filled, readOnly, classNamesPlaceholder]);
    const setRows = React__namespace.useCallback(() => {
        if (!inputRef.current) {
            return;
        }
        const textareaLineHeight = 20;
        const previousRows = inputRef.current.rows;
        inputRef.current.rows = rows ?? MIN_ROWS;
        const currentRows = ~~(inputRef.current.scrollHeight / textareaLineHeight);
        if (currentRows === previousRows) {
            inputRef.current.rows = currentRows;
        }
        if (currentRows >= MAX_ROWS) {
            inputRef.current.rows = MAX_ROWS;
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
        currentRows && setInnerRows(currentRows < MAX_ROWS ? currentRows : MAX_ROWS);
    }, [inputRef, rows]);
    const handleClick = React__namespace.useCallback((event) => {
        inputRef.current?.focus();
        setFocused.on();
        onClick?.(event);
    }, [onClick, setFocused]);
    const handleFocus = React__namespace.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React__namespace.useCallback((event) => {
        if (event.relatedTarget === containerRef.current)
            return;
        setFocused.off();
        onBlur?.(event);
    }, [onBlur, setFocused]);
    const handleChange = React__namespace.useCallback((event) => {
        resizable && setRows();
        onChange?.(event);
    }, [resizable, setRows, onChange]);
    const handleContainerBlur = (event) => {
        // needs to prevent extra onBlur callback executing on Textarea wrapper
        const relTarget = event.relatedTarget;
        if (containerRef.current?.contains(relTarget)) {
            event.stopPropagation();
        }
    };
    const handleContainerFocus = (event) => {
        // needs to prevent extra onFocus callback executing on Textarea wrapper
        const relTarget = event.relatedTarget;
        if (relTarget === containerRef.current) {
            event.stopPropagation();
        }
    };
    React__namespace.useEffect(() => {
        if (value !== undefined && value !== '') {
            setFilled.on();
        }
        else {
            setFilled.off();
        }
    }, [setFilled, value]);
    React__namespace.useEffect(() => {
        resizable && setRows();
    }, [resizable, value, setRows]);
    return (React__namespace.createElement("div", { ref: containerRef, "data-qa": "Textarea", className: classNames, style: style, onClick: handleClick, onBlur: handleContainerBlur, onFocus: handleContainerFocus, tabIndex: -1 },
        React__namespace.createElement("textarea", { id: inputId, spellCheck: "false", rows: innerRows, ref: reactUtils.useMergedRef(inputRef, ref), value: TextInput.fixControlledValue(value), disabled: formControlContext.disabled || disabled, readOnly: readOnly, className: classNamesTextarea, onBlur: handleBlur, onFocus: handleFocus, onChange: handleChange, ...restProps }),
        labelElement,
        placeholderElement,
        iconElement));
});
Textarea.defaultProps = {
    rows: 1,
    resizable: true,
};
Textarea.displayName = 'Textarea';

exports.Textarea = Textarea;
//# sourceMappingURL=Textarea.js.map
